import { useCallback, useContext, useEffect, useRef } from "react";

import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import type { AsyncTask } from "@/modern/components/async-tasks/types";
import {
  isAsyncTaskType,
  matchBoardIncentiveEvaluationTask,
} from "@/modern/components/async-tasks/utils";
import { openExecutedIncentivesEditor } from "@/modern/utils";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import BryntumContext from "@/react/context/bryntumContext";

export function useIncentiveEvaluationCompletion() {
  const { project, editorHandler, selectedBoard } = useContext(BryntumContext);
  const { isReady, fetchResult, markSeen, register, getTasks } =
    useAsyncTaskContext();

  const boardId = (selectedBoard?.record as { id?: number } | undefined)?.id;
  const shownTaskIdsRef = useRef(new Set<number>());
  const initialScanDoneRef = useRef(false);

  const openModalForTasks = useCallback(
    async (tasks: AsyncTask[]) => {
      if (!project || boardId == null || !editorHandler) return;

      const candidates = tasks.filter(
        (task) =>
          isAsyncTaskType(task, ASYNC_TASK_TYPES.INCENTIVE_EVALUATION) &&
          matchBoardIncentiveEvaluationTask(task, boardId) &&
          task.status === "Completed" &&
          !task.is_seen &&
          !shownTaskIdsRef.current.has(task.id),
      );

      if (candidates.length === 0) return;

      for (const task of candidates) {
        shownTaskIdsRef.current.add(task.id);
      }

      await openExecutedIncentivesEditor({
        tasks: candidates,
        project,
        fetchResult,
        markSeen,
        editorHandler,
      });
    },
    [boardId, editorHandler, fetchResult, markSeen, project],
  );

  useEffect(() => {
    if (!isReady || boardId == null) return undefined;

    return register({
      filter: (task) => matchBoardIncentiveEvaluationTask(task, boardId),
      onCompleted: (task) => {
        void openModalForTasks([task]);
      },
    });
  }, [boardId, isReady, openModalForTasks, register]);

  useEffect(() => {
    initialScanDoneRef.current = false;
  }, [boardId]);

  useEffect(() => {
    if (!isReady || boardId == null || initialScanDoneRef.current) return;

    initialScanDoneRef.current = true;

    const unseenCompleted = getTasks().filter(
      (task) =>
        isAsyncTaskType(task, ASYNC_TASK_TYPES.INCENTIVE_EVALUATION) &&
        matchBoardIncentiveEvaluationTask(task, boardId) &&
        task.status === "Completed" &&
        !task.is_seen,
    );

    if (unseenCompleted.length > 0) {
      void openModalForTasks(unseenCompleted);
    }
  }, [boardId, getTasks, isReady, openModalForTasks]);
}
